import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ShieldCheck, Phone, Loader2, X } from 'lucide-react';
import {
  PhoneVerifyStatus,
  getPhoneVerifyStatus,
  sendPhoneOtp,
  confirmPhoneOtp,
} from '../../api/phone-verify/phone-verify.api';
import { LocalStorageService } from '../../configuration';
import { logoutAPI } from '../../api/authentication/login.api';

/**
 * Ép verify SĐT sau 3 ngày trial (yêu cầu sếp — "luật rồi"). App load gọi status:
 * - mustVerify → modal HARD-BLOCK toàn màn (không đóng được, chỉ verify hoặc logout).
 * - còn trial (chưa verify) → banner đếm ngược (đóng được, nhắc lại phiên sau).
 * ENFORCEMENT do BE bật/tắt (PhoneVerify:Enabled) → khi tắt, status.enabled=false, component ẩn hoàn toàn.
 */
const PhoneVerifyGate: React.FC = () => {
  const [status, setStatus] = useState<PhoneVerifyStatus | null>(null);
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem('pv-banner-dismissed') === '1'
  );

  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [devOtp, setDevOtp] = useState<string | null>(null);

  useEffect(() => {
    const token = LocalStorageService.getItem('accessToken');
    if (!token) return; // chưa đăng nhập → không gọi
    getPhoneVerifyStatus()
      .then(setStatus)
      .catch(() => undefined);
  }, []);

  if (!status || !status.enabled || status.phoneVerified) return null;

  const send = async () => {
    try {
      setBusy(true);
      const r = await sendPhoneOtp(phone.trim());
      if (r.alreadyVerified) {
        toast.success('Số điện thoại đã được xác minh.');
        setStatus({ ...status, phoneVerified: true });
        return;
      }
      setDevOtp(r.devOtp ?? null);
      setStep('otp');
      toast.success(r.isMock ? 'Đã tạo OTP (chế độ thử — xem mã gợi ý bên dưới).' : 'Đã gửi OTP tới số của bạn.');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Gửi OTP thất bại.');
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    try {
      setBusy(true);
      const r = await confirmPhoneOtp(phone.trim(), code.trim());
      if (r.verified) {
        toast.success('Xác minh số điện thoại thành công!');
        // Reload để token mới mang PhoneVerified=true + gỡ chặn.
        setTimeout(() => window.location.reload(), 600);
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Mã OTP không đúng hoặc đã hết hạn.');
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    // FULL logout: thu hồi refresh token + xoá cookie httpOnly cab_rt phía server (best-effort),
    // rồi xoá HẾT token localStorage. Nếu chỉ xoá accessToken, cookie cab_rt còn -> interceptor 401
    // tự refresh -> đăng nhập lại -> gate hiện lại (bug "bấm logout vẫn bị bắt hoàn thiện profile").
    try { await logoutAPI(); } catch { /* lỗi mạng vẫn logout phía client */ }
    LocalStorageService.removeItem?.('accessToken');
    LocalStorageService.removeItem?.('fingerprintHash');
    LocalStorageService.removeItem?.('refreshToken');
    window.location.href = '/account/login';
  };

  const form = (
    <div className="space-y-3">
      {step === 'phone' ? (
        <>
          <label className="text-sm ui-muted">Số điện thoại (Việt Nam)</label>
          <div className="flex items-center gap-2 rounded-lg ui-divider border px-3 py-2">
            <Phone size={16} className="ui-muted shrink-0" />
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && phone && send()}
              inputMode="tel"
              placeholder="0xxxxxxxxx"
              className="flex-1 bg-transparent outline-none ui-text"
            />
          </div>
          <button
            onClick={send}
            disabled={busy || !phone}
            className="w-full rounded-lg bg-[#0071A6] text-white py-2.5 font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : null} Gửi mã OTP
          </button>
        </>
      ) : (
        <>
          <label className="text-sm ui-muted">Nhập mã OTP đã gửi tới {phone}</label>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && code && confirm()}
            inputMode="numeric"
            placeholder="Mã 6 số"
            className="w-full rounded-lg ui-divider border px-3 py-2.5 tracking-widest text-center text-lg ui-text bg-transparent outline-none"
          />
          {devOtp ? (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              Chế độ thử: mã của bạn là <b>{devOtp}</b> (khi bật SMS thật sẽ gửi về điện thoại).
            </p>
          ) : null}
          <button
            onClick={confirm}
            disabled={busy || !code}
            className="w-full rounded-lg bg-[#0071A6] text-white py-2.5 font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : null} Xác minh
          </button>
          <button onClick={() => setStep('phone')} className="w-full text-sm ui-muted py-1">
            Đổi số / gửi lại
          </button>
        </>
      )}
    </div>
  );

  // HARD-BLOCK: hết hạn trial → chặn toàn màn hình.
  if (status.mustVerify) {
    return (
      <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm grid place-items-center p-4">
        <div className="w-full max-w-md rounded-2xl ui-card border ui-divider p-6 space-y-4 shadow-2xl">
          <div className="flex items-center gap-2 text-[#0071A6]">
            <ShieldCheck />
            <h2 className="text-lg font-bold ui-text">Xác minh số điện thoại để tiếp tục</h2>
          </div>
          <p className="text-sm ui-muted">
            Thời gian dùng thử đã kết thúc. Theo quy định của Cab.vn, bạn cần xác minh số điện thoại để tiếp tục sử dụng.
          </p>
          {form}
          <button onClick={logout} className="w-full text-xs ui-muted pt-1">
            Đăng xuất
          </button>
        </div>
      </div>
    );
  }

  // TRIAL: còn hạn, chưa verify → banner đếm ngược (đóng được).
  if (dismissed) return null;
  return (
    <div className="fixed bottom-3 inset-x-3 sm:left-auto sm:right-4 sm:w-[380px] z-[95] rounded-xl ui-card border ui-divider shadow-lg p-4 space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 text-[#0071A6]">
          <ShieldCheck size={18} />
          <span className="font-semibold ui-text text-sm">Xác minh số điện thoại</span>
        </div>
        <button
          onClick={() => {
            setDismissed(true);
            sessionStorage.setItem('pv-banner-dismissed', '1');
          }}
          className="ui-muted hover:opacity-70"
        >
          <X size={16} />
        </button>
      </div>
      <p className="text-xs ui-muted">
        {typeof status.daysLeft === 'number'
          ? `Còn ${status.daysLeft} ngày dùng thử. Hãy xác minh số điện thoại để dùng tiếp không gián đoạn.`
          : 'Hãy xác minh số điện thoại để dùng tiếp không gián đoạn.'}
      </p>
      {form}
    </div>
  );
};

export default PhoneVerifyGate;
